/**
 * Interactive Property View Component
 * Wrapper component that provides toggle between Site Plan and Building Facade views
 * Supports controlled mode for URL state persistence
 */
import { useState, useEffect } from 'react';
import { InteractiveSitePlan } from './InteractiveSitePlan';
import { InteractiveBuildingFacade } from './InteractiveBuildingFacade';
import type { InteractiveMapProps } from '@/types/map';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Button } from './ui/button';
import { Map, Building2, RotateCcw } from 'lucide-react';

export type ViewMode = 'site-plan' | 'building-facade';

interface InteractivePropertyViewProps extends InteractiveMapProps {
  // View mode (controlled)
  view?: ViewMode;
  onViewChange?: (view: ViewMode) => void;
  // Block selection (controlled)
  block?: 'A' | 'B';
  onBlockChange?: (block: 'A' | 'B') => void;
  // Filter callbacks for URL persistence
  onViewFilterChange?: (filter: string) => void;
  onAvailabilityFilterChange?: (filter: string) => void;
  onCategoryFilterChange?: (filter: string) => void;
  onFloorFilterChange?: (filter: string) => void;
  // Show units toggle (for building facade)
  showUnits?: boolean;
  onShowUnitsChange?: (show: boolean) => void;
  // Reset callback
  onReset?: () => void;
  // Legacy props
  defaultView?: ViewMode;
  showViewToggle?: boolean;
}

export function InteractivePropertyView({
  onUnitClick,
  selectedUnit,
  viewFilter = 'all',
  availabilityFilter = 'all',
  categoryFilter = 'all',
  floorFilter = 'all',
  // Controlled view mode
  view,
  onViewChange,
  // Controlled block
  block,
  onBlockChange,
  // Filter callbacks
  onViewFilterChange,
  onAvailabilityFilterChange,
  onCategoryFilterChange,
  onFloorFilterChange,
  // Show units toggle
  showUnits,
  onShowUnitsChange,
  // Reset
  onReset,
  // Legacy
  defaultView = 'site-plan',
  showViewToggle = true,
}: InteractivePropertyViewProps) {
  // Use controlled view if provided, otherwise use internal state
  const [internalView, setInternalView] = useState<ViewMode>(view || defaultView);
  const [internalBlock, setInternalBlock] = useState<'A' | 'B'>(block || 'A');

  // Sync internal state with controlled props
  useEffect(() => {
    if (view !== undefined) {
      setInternalView(view);
    }
  }, [view]);

  useEffect(() => {
    if (block !== undefined) {
      setInternalBlock(block);
    }
  }, [block]);

  const activeView = view !== undefined ? view : internalView;
  const activeBlock = block !== undefined ? block : internalBlock;

  const handleViewChange = (newView: ViewMode) => {
    if (view === undefined) {
      setInternalView(newView);
    }
    onViewChange?.(newView);
  };

  const handleBlockChange = (newBlock: 'A' | 'B') => {
    if (block === undefined) {
      setInternalBlock(newBlock);
    }
    onBlockChange?.(newBlock);
  };

  // Common props for both views
  const commonProps: InteractiveMapProps & {
    onViewFilterChange?: (filter: string) => void;
    onAvailabilityFilterChange?: (filter: string) => void;
    onCategoryFilterChange?: (filter: string) => void;
    onFloorFilterChange?: (filter: string) => void;
  } = {
    onUnitClick,
    selectedUnit,
    viewFilter,
    availabilityFilter,
    categoryFilter,
    floorFilter,
    onViewFilterChange,
    onAvailabilityFilterChange,
    onCategoryFilterChange,
    onFloorFilterChange,
  };

  // If toggle is disabled, just render the default view
  if (!showViewToggle) {
    return activeView === 'site-plan' ? (
      <InteractiveSitePlan {...commonProps} />
    ) : (
      <InteractiveBuildingFacade 
        {...commonProps} 
        block={activeBlock}
        onBlockChange={handleBlockChange}
        showUnits={showUnits}
        onShowUnitsChange={onShowUnitsChange}
      />
    );
  }

  return (
    <div className="space-y-4">
      <Tabs value={activeView} onValueChange={(v) => handleViewChange(v as ViewMode)} className="w-full">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <TabsList className="grid w-auto grid-cols-2">
              <TabsTrigger value="site-plan" className="flex items-center gap-2 px-4">
                <Map className="h-4 w-4" />
                <span className="hidden sm:inline">Site Plan</span>
              </TabsTrigger>
              <TabsTrigger value="building-facade" className="flex items-center gap-2 px-4">
                <Building2 className="h-4 w-4" />
                <span className="hidden sm:inline">Building View</span>
              </TabsTrigger>
            </TabsList>
            
            {/* Reset Button */}
            {onReset && (
              <Button
                variant="outline"
                size="sm"
                onClick={onReset}
                className="ml-2"
              >
                <RotateCcw className="h-4 w-4 mr-1" />
                <span className="hidden sm:inline">Reset</span>
              </Button>
            )}
          </div>
          
          {/* View description */}
          <p className="text-sm text-muted-foreground hidden md:block">
            {activeView === 'site-plan' 
              ? 'Bird\'s eye view of the development'
              : 'Front facade view of the building'}
          </p>
        </div>
        
        <TabsContent value="site-plan" className="mt-0">
          <InteractiveSitePlan {...commonProps} />
        </TabsContent>
        
        <TabsContent value="building-facade" className="mt-0">
          <InteractiveBuildingFacade 
            {...commonProps}
            block={activeBlock}
            onBlockChange={handleBlockChange}
            showUnits={showUnits}
            onShowUnitsChange={onShowUnitsChange}
          />
        </TabsContent>
      </Tabs>
    </div>
  );
}
